import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import ContentModal from '../../components/modals/ContentModal';
import { contentApiService } from '../../api/contentApi';
import { usersAPI } from '../../api/api';
import { FunnelIcon, PlusIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const CONTENT_TYPE_LABELS = {
  story: 'Hikaye',
  post: 'Post',
  reels: 'Reels',
  shorts: 'Shorts',
  video: 'Video',
  carousel: 'Karosel',
};

const ContentPlanner = () => {
  const [plans, setPlans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const [filters, setFilters] = useState({
    content_name: '',
    account_name: '',
    channel: '',
    content_type: '',
    share_date: '',
  });

  const referenceData = useMemo(
    () => ({
      channels,
      users,
    }),
    [channels, users]
  );

  useEffect(() => {
    loadReferences();
    loadPlans();
  }, []);

  const loadReferences = async () => {
    try {
      const [channelsResponse, usersResponse] = await Promise.all([
        contentApiService.getChannels(),
        usersAPI.getUsers(),
      ]);

      setChannels(channelsResponse?.success ? channelsResponse.data || [] : []);
      setUsers(
        usersResponse?.success
          ? (usersResponse.data || []).map((user) => ({
              ...user,
              name: user.full_name || user.email,
            }))
          : []
      );
    } catch (err) {
      setError('Referans veriler yüklenemedi.');
    }
  };

  const loadPlans = async (customFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters = {
        account_name: customFilters.account_name,
        channel: customFilters.channel,
        content_type: customFilters.content_type,
        share_date__gte: customFilters.share_date,
      };

      const response = await contentApiService.getPlans(apiFilters);
      const data = response?.success ? response.data || [] : [];

      const filtered = data.filter((item) => {
        if (!customFilters.content_name) return true;
        return item.content_name?.toLowerCase().includes(customFilters.content_name.toLowerCase());
      });

      setPlans(filtered);
    } catch (err) {
      setError('Content planları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    const defaultFilters = {
      content_name: '',
      account_name: '',
      channel: '',
      content_type: '',
      share_date: '',
    };
    setFilters(defaultFilters);
    loadPlans(defaultFilters);
  };

  const handleSave = async (data, selectedFiles = [], selectedPhotoGroup = 'post_grid') => {
    try {
      setLoading(true);
      const response = data.id
        ? await contentApiService.updatePlan(data.id, data)
        : await contentApiService.createPlan(data);

      if (response?.success) {
        const savedPlanId = response?.data?.id || data.id;
        if (savedPlanId && selectedFiles.length > 0) {
          await contentApiService.uploadPlanPhotos(savedPlanId, selectedFiles, selectedPhotoGroup);
        }
        setIsModalOpen(false);
        setSelectedPlan(null);
        loadPlans();
      } else {
        setError(response?.message || 'Kayıt sırasında hata oluştu.');
      }
    } catch (err) {
      setError('Sunucu hatası oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!photoId) return;
    try {
      await contentApiService.deletePlanPhoto(photoId);
      await loadPlans();
      if (selectedPlan?.id) {
        const refreshed = await contentApiService.getPlans();
        if (refreshed?.success) {
          const found = (refreshed.data || []).find((item) => item.id === selectedPlan.id);
          setSelectedPlan(found || null);
        }
      }
    } catch (err) {
      setError('Fotoğraf silinemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      setLoading(true);
      const response = await contentApiService.deletePlan(id);
      if (response?.success) {
        loadPlans();
      } else {
        setError(response?.message || 'Silme işlemi başarısız.');
      }
    } catch (err) {
      setError('Silme sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const resolveChannelName = (plan) => {
    if (typeof plan.channel === 'object' && plan.channel?.channel_name) return plan.channel.channel_name;
    const channel = channels.find((item) => item.id === plan.channel);
    return channel?.channel_name || '-';
  };

  return (
    <div className="space-y-4 md:space-y-6 px-3 sm:px-4 md:px-0">
      <ContentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPlan(null);
        }}
        onSave={handleSave}
        onDeletePhoto={handleDeletePhoto}
        initialData={selectedPlan}
        referenceData={referenceData}
      />

      <div className="md:px-6 pt-3 md:pt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Content Planner</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">İçerik planlarınızı yönetin.</p>
          </div>
        </div>
      </div>

      <div className="md:px-6">
        <div className="flex items-center justify-start">
          <Button
            onClick={() => setIsModalOpen(true)}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
            title="Yeni Content"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="md:px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        </div>
      )}

      <div className="md:px-6">
        <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FunnelIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Filters</h3>
              </div>
              <div className="flex items-center gap-2">
                {isFiltersOpen && (
                  <>
                    <Button onClick={clearFilters} variant="outline" className="px-3 py-1.5 text-sm">Clear</Button>
                    <Button onClick={() => loadPlans()} className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white">Search</Button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setIsFiltersOpen((prev) => !prev)}
                  className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  <span>Advanced Search</span>
                  <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {isFiltersOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <input
                value={filters.content_name}
                onChange={(e) => setFilters((prev) => ({ ...prev, content_name: e.target.value }))}
                placeholder="Content Name"
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <select
                value={filters.account_name}
                onChange={(e) => setFilters((prev) => ({ ...prev, account_name: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Accounts</option>
                <option value="Antedon">Antedon</option>
                <option value="Alinda">Alinda</option>
                <option value="Claros">Claros</option>
                <option value="Residence">Residence</option>
                <option value="Lush">Lush</option>
                <option value="Suites">Suites</option>
              </select>
              <select
                value={filters.channel}
                onChange={(e) => setFilters((prev) => ({ ...prev, channel: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Channels</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.channel_name}
                  </option>
                ))}
              </select>
              <select
                value={filters.content_type}
                onChange={(e) => setFilters((prev) => ({ ...prev, content_type: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Types</option>
                {Object.entries(CONTENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={filters.share_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, share_date: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Content Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Account</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Channel</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Share Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Creator</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsible</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading...</td>
                    </tr>
                  ) : plans.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No records found.</td>
                    </tr>
                  ) : (
                    plans.map((plan) => (
                      <tr
                        key={plan.id}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setIsModalOpen(true);
                        }}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-900 dark:text-white font-medium">{plan.content_name}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{plan.account_name || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{resolveChannelName(plan)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{plan.share_date || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{CONTENT_TYPE_LABELS[plan.content_type] || plan.content_type}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{plan.creator_user || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{plan.responsible_person_name || '-'}</td>
                        <td className="px-3 py-4 text-xs text-gray-600 dark:text-gray-300 max-w-[240px] truncate">{plan.description || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-xs">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(plan.id);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ContentPlanner;

